import { Button, Flex, Stack, Text } from '@mantine/core';
import * as React from 'react';

import { PdfDocumentNote } from '@/stores/local/shared/PdfNotesStore';

import DocumentNoteListItem from '../DocumentNoteListItem';

import DocumentAiSummaryGeneratingRow from './DocumentAiSummaryGeneratingRow';

import s from './DocumentNotesList.module.scss';

type DocumentNotesListProps = {
  isLoading: boolean;
  notes: PdfDocumentNote[];
  aiSummaryNote: PdfDocumentNote | null;
  isAiSummaryGenerating: boolean;
  aiSummaryStreamPreview: string;
  activeNoteId: string | null;
  canGenerateAiSummary: boolean;
  onAddNote: () => void;
  onOpenNote: (noteId: string) => void;
  onEditNote: (noteId: string) => void;
  onDeleteNote: (noteId: string) => void;
  onGenerateAiSummary: () => void;
};

const DocumentNotesList: React.FC<DocumentNotesListProps> = ({
  isLoading,
  notes,
  aiSummaryNote,
  isAiSummaryGenerating,
  aiSummaryStreamPreview,
  activeNoteId,
  canGenerateAiSummary,
  onAddNote,
  onOpenNote,
  onEditNote,
  onDeleteNote,
  onGenerateAiSummary,
}) => {
  if (isLoading) {
    return (
      <Flex className={s.root__empty} align="center" justify="center">
        <Text c="dimmed" size="sm">
          Загружаю заметки...
        </Text>
      </Flex>
    );
  }

  const hasNoUserNotes = notes.length === 0;
  const showEmptyState = hasNoUserNotes && !aiSummaryNote && !isAiSummaryGenerating;

  if (showEmptyState) {
    return (
      <Flex className={s.root__empty} direction="column" align="center" justify="center" gap="md">
        <Text c="dimmed" size="sm" ta="center">
          По этому документу пока нет заметок. Добавьте заметки — после этого можно сформировать саммари по заметкам.
        </Text>
        <Button size="md" onClick={onAddNote}>
          Добавить заметку
        </Button>
      </Flex>
    );
  }

  return (
    <Stack gap={8}>
      {isAiSummaryGenerating && <DocumentAiSummaryGeneratingRow streamPreview={aiSummaryStreamPreview} />}
      {aiSummaryNote && !isAiSummaryGenerating && (
        <DocumentNoteListItem
          key={aiSummaryNote.id}
          note={aiSummaryNote}
          isActive={activeNoteId === aiSummaryNote.id}
          onOpen={onOpenNote}
          onEdit={onEditNote}
          onDelete={onDeleteNote}
          onRegenerate={canGenerateAiSummary ? onGenerateAiSummary : undefined}
        />
      )}
      {notes.map((note) => (
        <DocumentNoteListItem
          key={note.id}
          note={note}
          isActive={activeNoteId === note.id}
          onOpen={onOpenNote}
          onEdit={onEditNote}
          onDelete={onDeleteNote}
        />
      ))}
    </Stack>
  );
};

export default React.memo(DocumentNotesList);
